import { useCallback } from 'react';

import {
	AgentService,
	type Agent,
} from '../../../automation/services/agentService';
import { useTranslation } from '../../../providers/TranslationProvider';
import { EntitySelectorBase } from './EntitySelectorBase';
import type { ConversationAgentSelectorProps } from './types';

/**
 * Single-pick AI agent (`ConversationAgentEntity`) selector. Wraps
 * `EntitySelectorBase` with `AgentService.getAgents` for search +
 * `AgentService.getAgent` for ID resolution. Filters to active
 * agents only — disabled agents shouldn't be assignable as a
 * destination or fallback.
 */
export function ConversationAgentSelector({
	value,
	onChange,
	placeholder,
	disabled,
	error,
	allowClear,
	className,
	excludeIds,
}: ConversationAgentSelectorProps): JSX.Element {
	const { t } = useTranslation();

	const loadItems = useCallback(
		async (query: string): Promise<Array<Agent>> => {
			const result = await AgentService.getAgents({
				page: 1,
				pageSize: 20,
				filters: query !== '' ? { agentSearch: query } : undefined,
			});
			if (!result.success || result.data == null) return [];
			return result.data.filter(
				(a: { isActive?: boolean | number }) =>
					a.isActive === true || a.isActive === 1,
			);
		},
		[],
	);

	const loadItemById = useCallback(
		async (id: number): Promise<Agent | null> => {
			const result = await AgentService.getAgent(id);
			if (result.data == null) return null;
			const agent = result.data as { isActive?: boolean | number };
			if (agent.isActive !== true && agent.isActive !== 1) return null;
			return result.data;
		},
		[],
	);

	const mapToOption = useCallback(
		(agent: Agent): { id: number; label: string; subtitle?: string } => ({
			id: agent.id,
			label: agent.name ?? `Agent ${agent.id}`,
			subtitle:
				typeof (agent as { agent_type?: string }).agent_type === 'string'
					? AgentService.getAgentTypeLabel(
							(agent as { agent_type: string }).agent_type,
						)
					: undefined,
		}),
		[],
	);

	return (
		<EntitySelectorBase<Agent>
			value={value}
			onChange={onChange}
			placeholder={placeholder ?? t('Search AI agents...')}
			disabled={disabled}
			error={error}
			allowClear={allowClear}
			className={className}
			loadItems={loadItems}
			loadItemById={loadItemById}
			mapToOption={mapToOption}
			excludeIds={excludeIds}
		/>
	);
}
